/**
 * PlateNoteEditor — thin lazy-loading wrapper around PlateNoteEditorImpl.
 *
 * WHY THIS FILE EXISTS:
 * The static import chain
 *   entityTypeRegistry → NoteRecordOverview → NoteCard → MemoForm.tsx → PlateNoteEditor
 * was loading the entire Plate.js plugin ecosystem (all Plate.js packages)
 * at module-init time. In Vitest shard 1, combined with happy-dom and parallel transforms,
 * this caused the worker process to hang silently until the 10-minute CI timeout.
 *
 * This wrapper breaks the chain: the static import path stays the same for all consumers,
 * but Plate.js only loads when PlateNoteEditor is first rendered (dynamic import).
 */

import { lazy, type ReactNode } from 'react';

import { LazyRouteBoundary } from '@/lib/components/LazyRouteBoundary';

import type { PlateNoteEditorProps } from './PlateNoteEditorImpl';

export type { PlateNoteEditorProps };
// NOTE: preloading lives in ./preloadPlateNoteEditor (a component-free
// module) — import it from there. Keeping it out of this file lets this
// module stay component-only for react-refresh. Both use the same
// `./PlateNoteEditorImpl` specifier, so a preload warms the very chunk this
// `lazy()` resolves.

const PlateNoteEditorImpl = lazy(() =>
  import('./PlateNoteEditorImpl').then(m => ({ default: m.PlateNoteEditor }))
);

export function PlateNoteEditor({
  fallback,
  ...props
}: PlateNoteEditorProps & {
  /**
   * Rendered while the Plate chunk resolves. For the seamless inline editor,
   * pass the note's READ view here so a click-to-edit never blanks — with the
   * chunk preloaded this fallback shows for at most a frame, and because it's
   * the same text at the same size there's no visible flash. Defaults to null
   * (the standalone editor, which mounts inside its own already-visible form).
   */
  fallback?: ReactNode;
}) {
  return (
    <LazyRouteBoundary
      suspenseFallback={fallback ?? null}
      name='plate-note-editor'
    >
      <PlateNoteEditorImpl {...props} />
    </LazyRouteBoundary>
  );
}
